import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { ImageFile } from 'react-dropzone';
import { useWatch } from 'react-hook-form';
import { WppInlineMessage, WppObjectDivider, WppFileUpload } from 'buildingBlocks';
import { dataUploader } from '../style';
import { FileStates as FileStatesEnum } from '../constants';
import FileStates from './FileStates';
import FileToUpload from './FileToUpload';
import { CSVFile, ExternalDataForm } from '../types';

const { uploaderSection, fileText } = dataUploader;

const FileUploaderSection = () => {
  const [reader, setReader] = useState<FileReader>(new FileReader());
  const [fileUploadState, setFileUploadState] = useState<FileStatesEnum>(FileStatesEnum.initial);
  const [uploadedFile, setUploadedFile] = useState<Array<CSVFile>>([]);
  const [csvFile, setCsvFile] = useState<CSVFile>(null);
  const fileRef = useRef(null);

  const fileName = useWatch<ExternalDataForm>({ name: 'fileName' });
  const disableDrop = _.isEqual(FileStatesEnum.uploading, fileUploadState)
    || (_.isEqual(FileStatesEnum.initial, fileUploadState) && !!csvFile);

  useEffect(() => {
    if (!reader) {
      setReader(new FileReader());
    }
  }, [reader]);

  const onDrop = (accepted: Array<ImageFile>) => {
    const file = _.first(accepted);
    if (file && file instanceof Blob) {
      reader.onload = () => {
        const csvData = reader.result;
        setCsvFile(_.assign(file, { data: csvData }));
      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.readAsText(file, 'UTF-8');
    }
  };

  const resetFileState = () => {
    setCsvFile(null);
    setFileUploadState(FileStatesEnum.initial);
    setUploadedFile([]);
  };

  const deleteFile = () => {
    fileRef.current?.reset();
  };

  const handleFileUploadChange = (event: CustomEvent) => {
    resetFileState();
    const fileUploadValue = event.detail.value;
    setUploadedFile(fileUploadValue);
    onDrop(fileUploadValue);
  };

  return (
    <div style={uploaderSection}>
      <WppFileUpload
        ref={fileRef}
        onWppChange={handleFileUploadChange}
        value={uploadedFile}
        disabled={disableDrop}
        onWppFileUploadItemDelete={resetFileState}
        acceptConfig={{ 'text/csv': ['.csv'] }}
        className="file-upload-section"
        maxFiles={1}
        locales={{
          label: 'Choose a file',
          text: 'or drag it here',
          info: () => 'Only .csv file is accepted',
          sizeError: 'File exceeds size limit',
          formatError: 'Validation Failed. File format is not .csv. Please resolve the issue and try uploading again.',
        }}
      />
      {csvFile && (
        <FileStates
          file={csvFile}
          onDelete={resetFileState}
          fileUploadState={fileUploadState}
          setFileUploadState={setFileUploadState}
          setUploadedFile={setUploadedFile}
          deleteFile={deleteFile}
        />
      )}
      <WppObjectDivider style={{ margin: 0 }} />
      {_.isEmpty(fileName)
        ? (<div style={fileText}><WppInlineMessage type="information" size="s" message="No file uploaded yet" /> </div>)
        : (
          <FileToUpload
            fileName={fileName}
            fileUploadState={fileUploadState}
          />
        )}
    </div>
  );
};

export default FileUploaderSection;
